/* Degen Fits — product.js (loaded on product templates only)
   Variant resolution, gallery, sticky add-to-cart. Progressive: without this
   file the noscript variant <select> and native form remain fully purchasable. */
(() => {
  'use strict';
  const DF = window.DegenFits || { announce: () => {}, formatMoney: (v) => v, t: () => '', globals: { strings: {} } };
  const t = (key, repl) => {
    let s = (DF.globals.strings && DF.globals.strings[key]) || '';
    if (repl) for (const [k, v] of Object.entries(repl)) s = s.replace(`[${k}]`, v);
    return s;
  };

  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        const dataEl = this.querySelector('[data-variant-data]');
        if (!dataEl) return;
        this.initialized = true;
        this.variants = JSON.parse(dataEl.textContent);
        this.optionCount = parseInt(this.getAttribute('data-option-count'), 10) || 0;
        this.section = this.closest('[data-product-section]');
        this.form = this.section ? this.section.querySelector('form[data-product-form]') : null;
        this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        this.errorEl = this.section ? this.section.querySelector('[data-form-error]') : null;
        this.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) this.update();
        });
        this.update();
      }
      fieldsets() { return Array.from(this.querySelectorAll('[data-option-index]')); }
      selectedOptions() {
        return this.fieldsets().map((fs) => {
          const checked = fs.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }
      matchFor(selected) {
        return this.variants.find((variant) => variant.options.every((value, index) => selected[index] === value));
      }
      update() {
        const selected = this.selectedOptions();
        this.markAvailability(selected);
        this.updateLegends(selected);
        const complete = selected.every((value) => value !== null);
        const variant = complete ? this.matchFor(selected) : null;
        this.currentVariant = variant || null;
        if (this.idInput) {
          if (variant) {
            this.idInput.value = String(variant.id);
            this.idInput.disabled = false;
          } else {
            this.idInput.value = '';
            this.idInput.disabled = true;
          }
        }
        this.updatePrice(variant);
        this.updateButton(complete, variant);
        this.updateUrl(variant);
        if (variant && this.errorEl && !this.errorEl.hidden) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
        if (variant && variant.featured_media_id) {
          this.dispatchEvent(new CustomEvent('variant:media', { bubbles: true, detail: { mediaId: variant.featured_media_id } }));
        }
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, complete } }));
      }
      markAvailability(selected) {
        this.fieldsets().forEach((fs, optionIndex) => {
          fs.querySelectorAll('.option-value').forEach((wrap) => {
            const input = wrap.querySelector('input');
            const value = input.value;
            const anyAvailable = this.variants.some((variant) => {
              if (variant.options[optionIndex] !== value) return false;
              if (!variant.available) return false;
              return variant.options.every((optionValue, index) => {
                if (index === optionIndex) return true;
                return selected[index] === null || selected[index] === optionValue;
              });
            });
            wrap.classList.toggle('option-value--unavailable', !anyAvailable);
            const srState = wrap.querySelector('[data-option-state]');
            if (srState) srState.textContent = anyAvailable ? '' : `(${t('soldOut')})`;
          });
        });
      }
      updateLegends(selected) {
        this.fieldsets().forEach((fs, index) => {
          const valueEl = fs.querySelector('[data-selected-value]');
          if (valueEl) valueEl.textContent = selected[index] ? `— ${selected[index]}` : '';
        });
      }
      updatePrice(variant) {
        if (!this.section) return;
        const priceEl = this.section.querySelector('[data-price-current]');
        const compareEl = this.section.querySelector('[data-price-compare]');
        if (!variant || !priceEl) return;
        priceEl.textContent = DF.formatMoney(variant.price);
        if (compareEl) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compareEl.hidden = !onSale;
          if (onSale) {
            const compareAmount = compareEl.querySelector('[data-price-compare-amount]');
            if (compareAmount) compareAmount.textContent = DF.formatMoney(variant.compare_at_price);
          }
        }
      }
      updateButton(complete, variant) {
        if (!this.form) return;
        const button = this.form.querySelector('[data-submit]');
        if (!button) return;
        if (!complete) {
          button.removeAttribute('aria-disabled');
          button.textContent = t('addToCart');
        } else if (!variant) {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = t('unavailable');
        } else if (!variant.available) {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = t('soldOut');
        } else {
          button.removeAttribute('aria-disabled');
          button.textContent = t('addToCart');
        }
      }
      updateUrl(variant) {
        if (!variant || !window.history.replaceState) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      missingNames() {
        return this.fieldsets()
          .filter((fs) => !fs.querySelector('input:checked'))
          .map((fs) => fs.getAttribute('data-option-name'))
          .join(', ');
      }
      reportMissing() {
        const missing = this.fieldsets().filter((fs) => !fs.querySelector('input:checked'));
        if (!missing.length) {
          if (this.currentVariant && !this.currentVariant.available) {
            if (this.errorEl) {
              this.errorEl.textContent = t('soldOut');
              this.errorEl.hidden = false;
            }
            return true;
          }
          return false;
        }
        if (this.errorEl) {
          this.errorEl.textContent = t('optionsMissing', { names: this.missingNames() });
          this.errorEl.hidden = false;
        }
        const first = missing[0];
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: false });
        DF.announce(t('optionsMissing', { names: this.missingNames() }));
        return true;
      }
    });
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.stageItems = Array.from(this.querySelectorAll('[data-media-id]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb-for]'));
        this.statusEl = this.querySelector('[data-gallery-status]');
        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-thumb-for]');
          if (thumb) this.show(thumb.getAttribute('data-thumb-for'));
          const nav = event.target.closest('[data-gallery-nav]');
          if (nav) this.step(parseInt(nav.getAttribute('data-gallery-nav'), 10));
        });
        const section = this.closest('[data-product-section]');
        if (section) {
          section.addEventListener('variant:media', (event) => this.show(String(event.detail.mediaId)));
        }
      }
      activeIndex() { return this.stageItems.findIndex((item) => item.hasAttribute('data-active')); }
      step(delta) {
        if (!this.stageItems.length) return;
        const next = (this.activeIndex() + delta + this.stageItems.length) % this.stageItems.length;
        this.show(this.stageItems[next].getAttribute('data-media-id'));
      }
      show(mediaId) {
        let found = false;
        this.stageItems.forEach((item) => {
          const match = item.getAttribute('data-media-id') === String(mediaId);
          item.toggleAttribute('data-active', match);
          if (match) found = true;
        });
        if (!found) return;
        this.thumbs.forEach((thumb) => {
          const match = thumb.getAttribute('data-thumb-for') === String(mediaId);
          thumb.setAttribute('aria-current', match ? 'true' : 'false');
          if (match) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
        const index = this.activeIndex();
        if (this.statusEl && index >= 0) {
          const template = t('galleryStatus', { index: index + 1, count: this.stageItems.length });
          this.statusEl.textContent = template || `${index + 1} / ${this.stageItems.length}`;
        }
        const pause = this.querySelectorAll('video');
        pause.forEach((video) => { if (!video.closest('[data-active]')) video.pause(); });
      }
    });
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded || !this.dataset.url) return;
        const load = async () => {
          try {
            const response = await fetch(this.dataset.url);
            if (!response.ok) return;
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim().length) {
              this.dataset.loaded = 'true';
              this.innerHTML = fresh.innerHTML;
            }
          } catch (error) { /* leave hidden: recommendations are optional */ }
        };
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              observer.disconnect();
              load();
            }
          }, { rootMargin: '400px 0px' });
          observer.observe(this);
        } else {
          load();
        }
      }
    });
  }

  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        const section = this.closest('[data-product-section]') || document;
        const anchor = section.querySelector('[data-buy-anchor]');
        const footer = document.querySelector('.footer');
        if (!anchor || !('IntersectionObserver' in window)) return;
        this.pastAnchor = false;
        this.nearFooter = false;
        this.anchorObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.pastAnchor = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          });
          this.sync();
        }, { threshold: 0 });
        this.anchorObserver.observe(anchor);
        if (footer) {
          this.footerObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => { this.nearFooter = entry.isIntersecting; });
            this.sync();
          }, { threshold: 0 });
          this.footerObserver.observe(footer);
        }
        /* Mobile-compat round (M-9): IO notifications can strand the state on
           instant scroll jumps (End key, find-in-page). Recompute both flags
           from layout truth when scrolling settles — scrollend where the
           engine has it, else a passive throttled scroll sampler. IO stays
           the primary driver; this is a cheap corrector: two rect reads,
           no allocations per event. Same geometry as the observers:
           pastAnchor = anchor entirely above the viewport, nearFooter =
           footer box intersects the viewport. */
        this.recompute = () => {
          this.pastAnchor = anchor.getBoundingClientRect().bottom <= 0;
          if (footer) {
            const fr = footer.getBoundingClientRect();
            this.nearFooter = fr.top < window.innerHeight && fr.bottom > 0;
          }
          this.sync();
        };
        this.lastSampleAt = 0;
        this.onScrollSample = () => {
          const now = Date.now();
          if (now - this.lastSampleAt < 200) return;
          this.lastSampleAt = now;
          this.recompute();
        };
        this.usesScrollEnd = 'onscrollend' in window;
        if (this.usesScrollEnd) window.addEventListener('scrollend', this.recompute, { passive: true });
        else window.addEventListener('scroll', this.onScrollSample, { passive: true });
        section.addEventListener('variant:change', (event) => {
          const priceEl = this.querySelector('[data-sticky-price]');
          if (priceEl && event.detail.variant) priceEl.textContent = DF.formatMoney(event.detail.variant.price);
          const button = this.querySelector('[data-sticky-submit]');
          if (button && event.detail.complete) {
            const variant = event.detail.variant;
            if (!variant) {
              button.setAttribute('aria-disabled', 'true');
              button.textContent = t('unavailable');
            } else if (!variant.available) {
              button.setAttribute('aria-disabled', 'true');
              button.textContent = t('soldOut');
            } else {
              button.removeAttribute('aria-disabled');
              button.textContent = t('addToCart');
            }
          }
        });
      }
      disconnectedCallback() {
        if (this.anchorObserver) this.anchorObserver.disconnect();
        if (this.footerObserver) this.footerObserver.disconnect();
        if (this.recompute && this.usesScrollEnd) window.removeEventListener('scrollend', this.recompute);
        else if (this.onScrollSample) window.removeEventListener('scroll', this.onScrollSample);
      }
      sync() { this.toggleAttribute('data-visible', this.pastAnchor && !this.nearFooter); }
    });
  }
})();
